import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button, Col, Row, Spinner, Form } from "react-bootstrap";
import {
  HiOutlineAcademicCap,
  HiOutlineArrowLeft,
  HiOutlineBriefcase,
  HiOutlineCalendarDays,
  HiOutlineEnvelope,
  HiOutlineHeart,
  HiOutlineHome,
  HiOutlineIdentification,
  HiOutlineMapPin,
  HiOutlinePhone,
  HiOutlinePencilSquare,
  HiOutlineQuestionMarkCircle,
  HiOutlineUser,
  HiOutlineUsers,
  HiPlus,   
} from "react-icons/hi2";
import AdminTemplate from "../../../components/admin/layouts/Template";
import ContentWrapper from "../../../components/common/ContentWrapper";
import PageSpinner from "../../../components/common/spinner/PageSpinner";
import { routes } from "../../../helper/routes";
import { history } from "../../../helper/history";
import { ManageDashboardService } from "../../../service/manage/dashboard/manage_dashboard.service";
import { formFixture } from "../hierarchy/FormFixture";
import { ParentDashboardService } from "../../../service/manage/dashboard/parent_dashboard.service";
import { StudentService } from "../../../service/manage/registration/student.service";
import { TeacherService } from "../../../service/manage/registration/teacher.service";
import { AdminService } from "../../../service/manage/registration/admin.service";
import { GenderService } from "../../../service/settings/manage/gender.service";
import { StatusService } from "../../../service/settings/manage/status.service";
import { assessmentService } from "../../../service/assessment/assessmentService";
import { GradeService } from "../../../service/settings/manage/grade.service";
import { StatusTypesService } from "../../../service/settings/manage/status_type.service";
import { StudentAddressService } from "../../../service/manage/registration/student_address.service";
import { CountryService } from "../../../service/settings/manage/country.service.js";
import { ProvinceService } from "../../../service/settings/manage/province.service";
import { CityService } from "../../../service/settings/manage/city.service";
import { sweetStatusAlert } from "../../../components/common/SweetAlert";
import Alert from "@mui/material/Alert";
import "./MyProfile.css";

const SUPPORT_NUMBER = "+94 778 548 796";

const PERMANENT_ADDRESS_TYPE_ID = 1;
const BILLING_ADDRESS_TYPE_ID = 2;
const SHIPPING_ADDRESS_TYPE_ID = 3;
const ADDRESS_TYPE_PRIORITY = [PERMANENT_ADDRESS_TYPE_ID, BILLING_ADDRESS_TYPE_ID];

const ADDRESS_TYPES = [
  { key: "permanent", type_id: PERMANENT_ADDRESS_TYPE_ID, label: "Permanent Address" },
  { key: "billing", type_id: BILLING_ADDRESS_TYPE_ID, label: "Billing Address" },
  { key: "shipping", type_id: SHIPPING_ADDRESS_TYPE_ID, label: "Shipping Address" },
];

const EMPTY_ADDRESS_FORM = {
  student_address_id: null,
  address_1: "",
  address_2: "",
  country_id: "",
  province_id: "",
  city_id: "",
  postalcode: "",
};

const hasMeaningfulAddressData = (addr) =>
  !!addr && Boolean(addr.address_1 || addr.address_2 || addr.city_id || addr.province_id || addr.country_id || addr.postalcode);

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return value;
};

const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getDisplayValue(value);

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const calculateAge = (dob) => {
  if (!dob) return null;

  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const toTitleCase = (value) => {
  if (!value) return "N/A";
  return value
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const extractList = (response, keys = []) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload;

  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const DetailItem = ({ icon: Icon, label, value, name, editable, editMode, onChange, inputType, options, displayValue }) => (
  <div className="profile-detail-item h-100">
    <div className="profile-detail-icon">
      <Icon />
    </div>
    <div className="w-100 pe-2">
      <div className="profile-detail-label d-flex align-items-center">
        {label}
      </div>
      {editMode && editable ? (
        inputType === "select" ? (
          <Form.Select
            name={name}
            size="sm"
            value={value || ""}
            onChange={onChange}
            className="mt-1 profile-inline-input"
          >
            <option value="">Select {label}</option>
            {options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={name === "dob" ? "date" : "text"}
            name={name}
            size="sm"
            value={value || ""}
            onChange={onChange}
            className="mt-1 profile-inline-input"
          />
        )
      ) : (
        <div className="profile-detail-value">
          {displayValue ? displayValue : name === "dob" ? formatDate(value) : getDisplayValue(value)}
        </div>
      )}
    </div>
  </div>
);

const RelationshipCard = ({ icon: Icon, title, subtitle, meta }) => (
  <div className="profile-relationship-card h-100">
    <div className="profile-relationship-icon">
      <Icon />
    </div>
    <div className="profile-relationship-title">{getDisplayValue(title)}</div>
    <div className="profile-relationship-subtitle">{getDisplayValue(subtitle)}</div>
    <div className="profile-relationship-meta">{getDisplayValue(meta)}</div>
  </div>
);

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="profile-section-card">
    <div className="profile-section-header">
      <div className="profile-section-title-wrap">
        <span className="profile-section-badge">
          <Icon />
        </span>
        <h3 className="profile-section-title">{title}</h3>
      </div>
    </div>
    {children}
  </div>
);

const MyProfile = () => {
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(true);
  const [parentDetails, setParentDetails] = useState([]);

  const userType = sessionStorage.getItem("userType");
  const email = sessionStorage.getItem("email");
  const userId = sessionStorage.getItem("manage_user_id");
  const tableLogId = sessionStorage.getItem("table_log_id");
  const userName = sessionStorage.getItem("username");
  const [userFirstName = "N/A", userLastName = "N/A"] = (userName || "").split(" ");
  const isEditableUser = ["student", "teacher", "admin"].includes(userType);

  const [userData, setUserData] = useState(null);
  const [rawStuId, setRawStuId] = useState(null);
  const [formData, setFormData] = useState({
    first_name: userFirstName,
    last_name: userLastName,
    phone_number: "",
    dob: "",
    idValue: "",
    grade: "",
    medium: "",
    school: "",
    gender_id: "",
    email: email || "",
    user_code: "",
    user_status_id: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [addressDirty, setAddressDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [genderOptions, setGenderOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [addressData, setAddressData] = useState({ permanent: null, billing: null, shipping: null });
  const [addressFormData, setAddressFormData] = useState({
    permanent: { ...EMPTY_ADDRESS_FORM },
    billing: { ...EMPTY_ADDRESS_FORM },
    shipping: { ...EMPTY_ADDRESS_FORM },
  });
  const [addressVisibility, setAddressVisibility] = useState({
    permanent: false,   
    billing: false,
    shipping: false,
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState({ permanent: [], billing: [], shipping: [] });
  const [cityOptions, setCityOptions] = useState({ permanent: [], billing: [], shipping: [] });
  const formSectionRef = useRef(null);

  const loadProvinces = async (typeKey, countryId) => {
  if (!countryId) {
    setProvinceOptions((prev) => ({ ...prev, [typeKey]: [] }));
    return;
  }
  try {
    const res = await ProvinceService.getProvinceForCountry(countryId);
    if (res?.success && res?.data) {
      setProvinceOptions((prev) => ({
        ...prev,
        [typeKey]: res.data.map(p => ({ value: p.province_id, label: p.name })),
      }));
    } else {
      setProvinceOptions((prev) => ({ ...prev, [typeKey]: [] }));
    }
  } catch (err) {
    setProvinceOptions((prev) => ({ ...prev, [typeKey]: [] }));
  }
};

const loadCities = async (typeKey, provinceId) => {
  if (!provinceId) {
    setCityOptions((prev) => ({ ...prev, [typeKey]: [] }));
    return;
  }
  try {
    const res = await CityService.getCitiesForProvince(provinceId);
    if (res?.success && res?.data) {
      setCityOptions((prev) => ({
        ...prev,
        [typeKey]: res.data.map(c => ({ value: c.city_id, label: c.name })),
      }));
    } else {
      setCityOptions((prev) => ({ ...prev, [typeKey]: [] }));
    }
  } catch (err) {
    setCityOptions((prev) => ({ ...prev, [typeKey]: [] }));
  }
};

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (!userType || (!userId && !email)) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        if (userType === "student" || userType === "teacher" || userType === "admin") {
          if (email) {
            let studentResult;
            if (userType === "student") {
              studentResult = await StudentService.getStudentbyEmail(email);
            } else if (userType === "teacher") {
              studentResult = await TeacherService.getByEmail(email);
            } else {
              studentResult = await AdminService.getByEmail(email);
            }
            if (isMounted && studentResult.success) {
              setUserData(studentResult.data);
              setRawStuId(studentResult.data.stu_id);
              setFormData({
                first_name: studentResult.data.first_name || userFirstName,
                last_name: studentResult.data.last_name || userLastName,
                phone_number: studentResult.data.phone_number || "",
                dob: studentResult.data.dob ? studentResult.data.dob.split("T")[0] : "",
                idValue: studentResult.data.idValue || "",
                grade: studentResult.data.grade || "",
                medium: studentResult.data.medium || "",
                school: studentResult.data.school || "",
                gender_id: studentResult.data.gender_id || "",
                email: studentResult.data.email || email || "",
                user_code: studentResult.data.user_code || "",
                user_status_id: studentResult.data.user_status_id || "",
              });
            }
          }
          if (userId) {
            const statusTypesResult = await StatusTypesService.getAll();
            const availabilityType = statusTypesResult?.data?.find(t => t.name === "Availability");
            const statusTypeId = availabilityType ? availabilityType.type_id : "";

            const languageResult = await assessmentService.getAssessmentLanguage();
            if (isMounted && languageResult.success) {
              const mappedMediums = languageResult.data.languages.map(lang => ({
                value: lang.language_id,
                label: lang.language,
              }));
              setMediumOptions(mappedMediums);
            }

            const gradeResult = await GradeService.getGrades();
            if (isMounted && gradeResult.success && gradeResult.data) {
              const mappedGrades = gradeResult.data.map(g => ({
                value: g.grade_id,
                label: String(g.grade),
              }));
              setGradeOptions(mappedGrades);
            }

            const promises = [
              GenderService.getAllGenders(),
              StatusService.getByTypeId(statusTypeId)
            ];
            if (userType === "student") {
              promises.push(ParentDashboardService.getAllParents(userId));
            }
            const results = await Promise.all(promises);
            if (userType === "student" && userId) {
  try {
    const addressResult = await StudentAddressService.getStudentAddressById(userId);

    // Country dropdown options are needed either way
    const countryResult = await CountryService.getAll();
    if (isMounted && countryResult?.data) {
      setCountryOptions(countryResult.data.map(c => ({ value: c.country_id, label: c.name })));
    }

    if (isMounted && addressResult?.success && addressResult?.data?.length > 0) {
      const newAddressData = { permanent: null, billing: null, shipping: null };
      const newAddressFormData = {
        permanent: { ...EMPTY_ADDRESS_FORM },
        billing: { ...EMPTY_ADDRESS_FORM },
        shipping: { ...EMPTY_ADDRESS_FORM },
      };

      const newAddressVisibility = { permanent: false, billing: false, shipping: false };

      for (const { key, type_id } of ADDRESS_TYPES) {
        const addr = addressResult.data.find((a) => a.student_address_type_id === type_id);
        if (addr) {
          newAddressData[key] = addr;
          newAddressFormData[key] = {
            student_address_id: addr.student_address_id,
            address_1: addr.address_1 || "",
            address_2: addr.address_2 || "",
            country_id: addr.country_id || "",
            province_id: addr.province_id || "",
            city_id: addr.city_id || "",
            postalcode: addr.postalcode || "",
          };
          if (hasMeaningfulAddressData(addr)) {
            newAddressVisibility[key] = true;
            if (addr.country_id) await loadProvinces(key, addr.country_id);
            if (addr.province_id) await loadCities(key, addr.province_id);
          }
        }
      }
      setAddressData(newAddressData);
      setAddressFormData(newAddressFormData);
      setAddressVisibility(newAddressVisibility);
    }
  } catch (err) {
    console.warn("Could not load student address:", err);
  }
}

            const gendersResult = results[0];
            const statusResult = results[1];
            
            if (isMounted) {
              if (userType === "student" && results[2]) {
                setParentDetails(extractList(results[2], ["parents"]));
              }
              
              if (gendersResult.success && gendersResult.data) {
                const allowedGenders = ["male", "female", "not to say"];
                let mappedGenders = gendersResult.data
                  .map(g => ({ value: g.gender_id || g.id, label: (g.name || g.gender_name || "").trim() }))
                  .filter(g => allowedGenders.includes(g.label.toLowerCase()));
                
                // Forcibly add them if the database is missing any of these specific options
                if (!mappedGenders.some(g => g.label.toLowerCase() === 'male')) {
                  mappedGenders.push({ value: 1, label: 'Male' });
                }
                if (!mappedGenders.some(g => g.label.toLowerCase() === 'female')) {
                  mappedGenders.push({ value: 2, label: 'Female' });
                }
                if (!mappedGenders.some(g => g.label.toLowerCase() === 'not to say')) {
                  mappedGenders.push({ value: 3, label: 'Not to say' });
                }

                setGenderOptions(mappedGenders);
              }

              if (statusResult.success && statusResult.data) {
                const mappedStatuses = statusResult.data.map(s => ({ value: s.status_id || s.id, label: s.name || s.status_name }));
                setStatusOptions(mappedStatuses);
              }
            }
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [email, userId, userType]);

  const profileName = useMemo(() => userName || "N/A", [userName]);

  const profileInitials = useMemo(() => {
    const names = profileName.split(" ").filter(Boolean);
    return names.slice(0, 2).map((name) => name[0]?.toUpperCase() || "").join("") || "U";
  }, [profileName]);

  const parentCards = parentDetails.length > 0 ? parentDetails : [];

  const handleEditProfileClick = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
  };

  const handleAddressInputChange = async (typeKey, e) => {
    const { name, value } = e.target;
    setAddressFormData((prev) => ({
      ...prev,
      [typeKey]: { ...prev[typeKey], [name]: value },
    }));
    setDirty(true);
    setAddressDirty(true);

    if (name === "country_id") {
      setAddressFormData((prev) => ({
        ...prev,
        [typeKey]: { ...prev[typeKey], country_id: value, province_id: "", city_id: "" },
      }));
      setCityOptions((prev) => ({ ...prev, [typeKey]: [] }));
      await loadProvinces(typeKey, value);
    }

    if (name === "province_id") {
      setAddressFormData((prev) => ({
        ...prev,
        [typeKey]: { ...prev[typeKey], province_id: value, city_id: "" },
      }));
      await loadCities(typeKey, value);
    }
  };

  const toggleAddressSection = (typeKey) => {
    setAddressVisibility((prev) => ({ ...prev, [typeKey]: true }));
  };

  const handleDiscard = () => {
    setIsEditing(false);
    setDirty(false);
    setAddressDirty(false);
    if (userData) {
      setFormData({
        first_name: userData.first_name || userFirstName,
        last_name: userData.last_name || userLastName,
        phone_number: userData.phone_number || "",
        dob: userData.dob ? userData.dob.split('T')[0] : "",
        idValue: userData.idValue || "",
        grade: userData.grade || "",
        medium: userData.medium || "",
        school: userData.school || "",
        gender_id: userData.gender_id || "",
        email: userData.email || email || "",
        user_code: userData.user_code || "",
        user_status_id: userData.user_status_id || "",
      });
    } else {
      setFormData({
        first_name: userFirstName,
        last_name: userLastName,
        phone_number: "",
        dob: "",
        idValue: "",
        grade: "",
        medium: "",
        school: "",
        gender_id: "",
        email: email || "",
        user_code: "",
        user_status_id: "",
      });
    }
    const resetAddressFormData = {};
    for (const { key } of ADDRESS_TYPES) {
      const addr = addressData[key];
      resetAddressFormData[key] = addr
        ? {
            student_address_id: addr.student_address_id,
            address_1: addr.address_1 || "",
            address_2: addr.address_2 || "",
            country_id: addr.country_id || "",
            province_id: addr.province_id || "",
            city_id: addr.city_id || "",
            postalcode: addr.postalcode || "",
          }
        : { ...EMPTY_ADDRESS_FORM };
    }
    setAddressFormData(resetAddressFormData);
    const resetAddressVisibility = { permanent: false, billing: false, shipping: false };
    for (const { key } of ADDRESS_TYPES) {
      if (hasMeaningfulAddressData(addressData[key])) resetAddressVisibility[key] = true;
    }
    setAddressVisibility(resetAddressVisibility);
  };

  const handleSaveChanges = async () => {
    if (!userData) return;

    if (!formData.first_name || !formData.first_name.trim()) {
      setValidationError("First Name is required.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!formData.last_name || !formData.last_name.trim()) {
      setValidationError("Last Name is required.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setValidationError("");
    setSaving(true);
    try {
      const studentItem = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        idValue: formData.idValue,
        idType: userData.idType ? parseInt(userData.idType) : null,
        email: formData.email,
        phone_number: formData.phone_number ? parseInt(formData.phone_number) : null,
        dob: formData.dob || null,
        gender_id: formData.gender_id ? parseInt(formData.gender_id) : null,
        created_on: userData.created_on || null,
        created_by: userData.created_by || formData.user_code,
        user_status_id: formData.user_status_id ? parseInt(formData.user_status_id) : null,
        node_mid: userData.node_mid,
        user_code: formData.user_code,
      };

      if (userType === "student") {
        studentItem.school = formData.school;
        studentItem.grade = formData.grade ? parseInt(formData.grade) : null;
        studentItem.medium = formData.medium ? parseInt(formData.medium) : null;
      } else if (userType === "teacher") {
        studentItem.role_code = userData.role_code;
        studentItem.country_id = userData.country_id ? parseInt(userData.country_id) : null;
        studentItem.province_id = userData.province_id ? parseInt(userData.province_id) : null;
        studentItem.city_id = userData.city_id ? parseInt(userData.city_id) : null;
        studentItem.address_1 = userData.address_1 || "";
        studentItem.address_2 = userData.address_2 || "";
        studentItem.postalcode = userData.postalcode ? parseInt(userData.postalcode) : null;
      } else if (userType === "admin") {
        studentItem.country_id = userData.country_id ? parseInt(userData.country_id) : null;
        studentItem.province_id = userData.province_id ? parseInt(userData.province_id) : null;
        studentItem.city_id = userData.city_id ? parseInt(userData.city_id) : null;
        studentItem.address_1 = userData.address_1 || "";
        studentItem.address_2 = userData.address_2 || "";
        studentItem.postalcode = userData.postalcode ? parseInt(userData.postalcode) : null;
      }

      let studentResponse;
      if (userType === "student") {
        if (addressDirty) {
          const relevantAddressTypes = ADDRESS_TYPES.filter(({ key }) => hasMeaningfulAddressData(addressFormData[key]));

          const toCreate = relevantAddressTypes
            .filter(({ key }) => !addressFormData[key].student_address_id)
            .map(({ key, type_id }) => {
              const form = addressFormData[key];
              return {
                student_id: rawStuId,
                student_address_type_id: type_id,
                address_1: form.address_1 || "",
                address_2: form.address_2 || "",
                country_id: form.country_id ? parseInt(form.country_id) : null,
                province_id: form.province_id ? parseInt(form.province_id) : null,
                city_id: form.city_id ? parseInt(form.city_id) : null,
                postalcode: form.postalcode || "",
                status: "active",
              };
            });

          const toUpdate = relevantAddressTypes
            .filter(({ key }) => addressFormData[key].student_address_id)
            .map(({ key, type_id }) => {
              const form = addressFormData[key];
              return {
                student_address_id: form.student_address_id,
                student_id: userId,
                student_address_type_id: type_id,
                address_1: form.address_1 || "",
                address_2: form.address_2 || "",
                country_id: form.country_id ? parseInt(form.country_id) : null,
                province_id: form.province_id ? parseInt(form.province_id) : null,
                city_id: form.city_id ? parseInt(form.city_id) : null,
                postalcode: form.postalcode || "",
                status: "active",
              };
            });

          if (toCreate.length > 0) {
            const createResponse = await StudentAddressService.create(JSON.stringify(toCreate));
            if (!createResponse?.success) {
              sweetStatusAlert("Failed!", createResponse?.message || "Failed to add residential address", "error");
            }
          }

          if (toUpdate.length > 0) {
            const updateResponse = await StudentAddressService.update(JSON.stringify(toUpdate));
            if (!updateResponse?.success) {
              sweetStatusAlert("Failed!", updateResponse?.message || "Failed to update residential address", "error");
            }
          }
        }

        studentResponse = await StudentService.update(JSON.stringify(studentItem), userId);
      } else if (userType === "teacher") {
        studentResponse = await TeacherService.update(studentItem, userId);
      } else if (userType === "admin") {
        studentResponse = await AdminService.update(studentItem, userId);
      }
      
      if (studentResponse && studentResponse.success) {
      sweetStatusAlert("Success!", "Profile updated successfully!", "success");
      setIsEditing(false);
      setDirty(false);
      setUserData((prev) => ({ ...prev, ...formData }));
      sessionStorage.setItem("username", `${formData.first_name} ${formData.last_name}`);

      if (userType === "student" && userId && addressDirty) {
        try {
          const refreshedAddressResult = await StudentAddressService.getStudentAddressById(userId);
          if (refreshedAddressResult?.success && refreshedAddressResult?.data?.length > 0) {
            const newAddressData = { permanent: null, billing: null, shipping: null };
            const newAddressFormData = {
              permanent: { ...EMPTY_ADDRESS_FORM },
              billing: { ...EMPTY_ADDRESS_FORM },
              shipping: { ...EMPTY_ADDRESS_FORM },
            };
            const newAddressVisibility = { permanent: false, billing: false, shipping: false };

            for (const { key, type_id } of ADDRESS_TYPES) {
              const addr = refreshedAddressResult.data.find((a) => a.student_address_type_id === type_id);
              if (addr) {
                newAddressData[key] = addr;
                newAddressFormData[key] = {
                  student_address_id: addr.student_address_id,
                  address_1: addr.address_1 || "",
                  address_2: addr.address_2 || "",
                  country_id: addr.country_id || "",
                  province_id: addr.province_id || "",
                  city_id: addr.city_id || "",
                  postalcode: addr.postalcode || "",
                };
                if (hasMeaningfulAddressData(addr)) {
                  newAddressVisibility[key] = true;
                  if (addr.country_id) await loadProvinces(key, addr.country_id);
                  if (addr.province_id) await loadCities(key, addr.province_id);
                }
              }
            }

            setAddressData(newAddressData);
            setAddressFormData(newAddressFormData);
            setAddressVisibility(newAddressVisibility);
          }
        } catch (err) {
          console.warn("Could not refresh address data after save:", err);
        }
        setAddressDirty(false);
      }
    } else {
      sweetStatusAlert("Failed!", studentResponse?.message || "Failed to update profile", "error");
    }
    } catch (e) {
      sweetStatusAlert("Failed!", "An error occurred while saving", "error");
    } finally {
      setSaving(false);
    }
  };

  const summaryItems = [
    { icon: HiOutlineEnvelope, value: email || "N/A" },
    { icon: HiOutlineCalendarDays, value: `Date of birth - ${formatDate(formData.dob)}` },
  ];

  const basicDetails = [
    { icon: HiOutlineUser, label: "First Name", value: formData.first_name, name: "first_name", editable: isEditableUser },
    { icon: HiOutlineUser, label: "Last Name", value: formData.last_name, name: "last_name", editable: isEditableUser },
    { icon: HiOutlineEnvelope, label: "Email", value: formData.email, name: "email", editable: isEditableUser },
    { icon: HiOutlinePhone, label: "Phone Number", value: formData.phone_number, name: "phone_number", editable: isEditableUser },
    { icon: HiOutlineCalendarDays, label: "Birthdate", value: formData.dob, name: "dob", editable: isEditableUser },
    { icon: HiOutlineIdentification, label: "ID Value", value: formData.idValue, name: "idValue", editable: isEditableUser },
    { icon: HiOutlineIdentification, label: userType === "student" ? "Student ID" : "User Code", value: formData.user_code, name: "user_code", editable: false },
    // { icon: HiOutlineIdentification, label: "Profile ID", value: userId || "N/A", name: "userId", editable: false },
  ];

  const getLabelFromOptions = (options, id) => {
    const found = options.find(o => String(o.value) === String(id));
    return found ? found.label : "N/A";
  };

  const getResidentialFieldsForType = (typeKey) => {
    const form = addressFormData[typeKey];
    const provinces = provinceOptions[typeKey] || [];
    const cities = cityOptions[typeKey] || [];

    return [
      { icon: HiOutlineHome, label: "Address Line 1", value: form.address_1, name: "address_1", editable: isEditableUser },
      { icon: HiOutlineHome, label: "Address Line 2", value: form.address_2, name: "address_2", editable: isEditableUser },
      {
        icon: HiOutlineMapPin,
        label: "Country",
        value: form.country_id,
        name: "country_id",
        editable: isEditableUser,
        inputType: "select",
        options: countryOptions,
        displayValue: getLabelFromOptions(countryOptions, form.country_id),
      },
      {
        icon: HiOutlineMapPin,
        label: "Province",
        value: form.province_id,
        name: "province_id",
        editable: isEditableUser,
        inputType: "select",
        options: provinces,
        displayValue: getLabelFromOptions(provinces, form.province_id),
      },
      {
        icon: HiOutlineMapPin,
        label: "City",
        value: form.city_id,
        name: "city_id",
        editable: isEditableUser,
        inputType: "select",
        options: cities,
        displayValue: getLabelFromOptions(cities, form.city_id),
      },
      { icon: HiOutlineMapPin, label: "Postal Code", value: form.postalcode, name: "postalcode", editable: isEditableUser },
    ];
  };

  const getGenderLabel = (id) => {
    const found = genderOptions.find(g => String(g.value) === String(id));
    return found ? found.label : "N/A";
  };

  const getGradeLabel = (id) => {
    const found = gradeOptions.find(g => String(g.value) === String(id));
    return found ? found.label : "N/A";
  };

  const getMediumLabel = (id) => {
    const found = mediumOptions.find(m => String(m.value) === String(id));
    return found ? found.label : "N/A";
  };

  const getStatusLabel = (id) => {
    const found = statusOptions.find(s => String(s.value) === String(id));
    return found ? found.label : "N/A";
  };

  const profileMetaDetails = [
    { 
      icon: HiOutlineUser, 
      label: "Gender", 
      value: formData.gender_id, 
      name: "gender_id", 
      editable: isEditableUser,
      inputType: "select",
      options: genderOptions,
      displayValue: getGenderLabel(formData.gender_id)
    },
    { 
      icon: HiOutlineHeart, 
      label: "Active Status", 
      value: formData.user_status_id, 
      name: "user_status_id", 
      editable: isEditableUser,
      inputType: "select",
      options: statusOptions,
      displayValue: getStatusLabel(formData.user_status_id)
    },
    { icon: HiOutlineBriefcase, label: "School", value: formData.school, name: "school", editable: isEditableUser },
    { icon: HiOutlineAcademicCap, label: "Grade", value: formData.grade, name: "grade", editable: isEditableUser, inputType: "select", options: gradeOptions, displayValue: getGradeLabel(formData.grade) },
    { icon: HiOutlineBriefcase, label: "Medium", value: formData.medium, name: "medium", editable: isEditableUser, inputType: "select", options: mediumOptions, displayValue: getMediumLabel(formData.medium) },
    { icon: HiOutlineCalendarDays, label: "Joined On", value: formatDate(userData?.created_at) || "N/A", name: "created_at", editable: false },
    // { icon: HiOutlineIdentification, label: "Table Log ID", value: tableLogId || "N/A" },
  ];

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }

    history.push(routes.manage_dashboard);
  };

  if (loading) {
    return (
      <AdminTemplate>
        <ContentWrapper>
          <div className="profile-loading-state">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        </ContentWrapper>
      </AdminTemplate>
    );
  }

  const tabs = [
    { key: "basic", label: "Basic details" },
    { key: "profile", label: "Profile Details" },
    ...(userType === "student" ? [{ key: "residential", label: "Residential Details" }] : []),
    ...(userType === "student" ? [{ key: "parent", label: "Parent Details" }] : []),
  ];

  return (
    <AdminTemplate>
      <ContentWrapper>
        {validationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {validationError}
          </Alert>
        )}
        <div className="my-profile-page">
          <div className="profile-page-header">
            <button className="profile-back-button" onClick={handleBack}>
              <HiOutlineArrowLeft />
            </button>
            <div>
              <h1 className="profile-page-title">Profile details</h1>
              <p className="profile-page-subtitle">
                Keep your profile up to date to get better matches.
              </p>
            </div>
          </div>

          <div className="profile-summary-card">
            <Row className="align-items-start">
              <Col lg={4} md={5} className="mb-4 mb-md-0">
                <div className="profile-avatar-panel">
                  <div className="profile-avatar-circle">{profileInitials}</div>
                </div>
              </Col>

              <Col lg={8} md={7}>
                <div className="profile-summary-content">
                  <div className="profile-summary-head">
                    <div>
                      <h2 className="profile-summary-name">{profileName}</h2>
                      <p className="profile-summary-age">
                        {toTitleCase(userType)}
                      </p>
                    </div>

                    {isEditableUser && (
                      <Button 
                        className={`profile-primary-button ${isEditing ? 'opacity-75 cursor-not-allowed' : ''}`} 
                        onClick={handleEditProfileClick}
                        disabled={isEditing}
                      >
                        <HiOutlinePencilSquare />
                        <span>{isEditing ? "Editing Mode" : "Edit Profile"}</span>
                      </Button>
                    )}
                  </div>

                  <div className="profile-help-card">
                    <div className="profile-help-icon">
                      <HiOutlineQuestionMarkCircle />
                    </div>
                    <div>
                      <div className="profile-help-title">Do you need help?</div>
                      <div className="profile-help-number">{SUPPORT_NUMBER}</div>
                    </div>
                  </div>

                  <div className="profile-summary-meta">
                    <Row>
                      {summaryItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <Col xl={6} lg={6} sm={12} key={`${item.value}-${index}`} className="mb-3">
                            <div className="profile-meta-item">
                              <Icon />
                              <span>{getDisplayValue(item.value)}</span>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <div className="profile-tabs-strip" ref={formSectionRef}>
            <div className="profile-tabs-list">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`profile-tab-button ${activeTab === tab.key ? "active" : ""}`}

                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "basic" && (
            <SectionCard icon={HiOutlineUser} title="Basic Details">
              <Row>
                {basicDetails.map((item) => (
                  <Col xl={3} md={6} key={item.label} className="mb-3">
                    <DetailItem 
                      {...item} 
                      editMode={isEditing} 
                      onChange={handleInputChange} 
                    />
                  </Col>
                ))}
              </Row>
              {isEditing && (
                <div className="profile-actions-wrapper border-top pt-4 mt-2">
                  <Button variant="outline-secondary" className="profile-discard-button" onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button className="profile-save-button" onClick={handleSaveChanges} disabled={saving || !dirty}>
                    {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                  </Button>
                </div>
              )}
            </SectionCard>
          )}

          {activeTab === "profile" && (
            <SectionCard icon={HiOutlineBriefcase} title="Profile Details">
              <Row>
                {profileMetaDetails.map((item) => (
                  <Col xl={4} md={6} key={item.label} className="mb-3">
                    <DetailItem 
                      {...item} 
                      editMode={isEditing} 
                      onChange={handleInputChange} 
                    />
                  </Col>
                ))}
              </Row>
              {isEditing && (
                <div className="profile-actions-wrapper border-top pt-4 mt-2">
                  <Button variant="outline-secondary" className="profile-discard-button" onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button className="profile-save-button" onClick={handleSaveChanges} disabled={saving || !dirty}>
                    {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                  </Button>
                </div>
              )}
            </SectionCard>
          )}

         {userType === "student" && activeTab === "residential" && (
            <>
              {!isEditing && !Object.values(addressVisibility).some(Boolean) ? (
                <SectionCard icon={HiOutlineHome} title="Residential Details">
                  <p className="profile-empty-state mb-0">No residential details have been added yet.</p>
                </SectionCard>
              ) : (
                ADDRESS_TYPES.map(({ key, label }) => {
                  const showAsForm = addressVisibility[key] || (isEditing && key === "permanent");
                  return showAsForm ? (
                    <SectionCard key={key} icon={HiOutlineHome} title={label}>
                      <Row>
                        {getResidentialFieldsForType(key).map((item) => (
                          <Col xl={4} md={6} key={`${key}-${item.label}`} className="mb-3">
                            <DetailItem
                              {...item}
                              editMode={isEditing}
                              onChange={(e) => handleAddressInputChange(key, e)}
                            />
                          </Col>
                        ))}
                      </Row>
                    </SectionCard>
                  ) : (
                    isEditing && (
                      <div
                        key={key}
                        className="profile-section-card d-flex align-items-center justify-content-center"
                        style={{ cursor: "pointer", minHeight: "80px" }}
                        onClick={() => toggleAddressSection(key)}
                      >
                        <div className="d-flex align-items-center gap-2 text-muted">
                          <HiPlus size={20} />
                          <span>Add {label}</span>
                        </div>
                      </div>
                    )
                  )
                })
              )}
              {isEditing && (
                <div className="profile-actions-wrapper border-top pt-4 mt-2">
                  <Button variant="outline-secondary" className="profile-discard-button" onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button className="profile-save-button" onClick={handleSaveChanges} disabled={saving || !dirty}>
                    {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                  </Button>
                </div>
              )}
            </>
          )}
          {userType === "student" && activeTab === "parent" && (
            <SectionCard icon={HiOutlineUsers} title="Parent Details">
              <Row>
                {parentCards.length > 0 ? (
                  parentCards.map((parent, index) => (
                    <Col lg={4} md={6} key={`${parent.parent_id || parent.email || index}`} className="mb-3">
                      <RelationshipCard
                        icon={HiOutlineUsers}
                        title={`${parent.first_name || "N/A"} ${parent.last_name || ""}`.trim()}
                        subtitle={parent.phone_number || "N/A"}
                        meta={parent.email || "N/A"}
                      />
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="profile-empty-state">Parent details are not available.</div>
                  </Col>
                )}
              </Row>
            </SectionCard>
          )}
        </div>
      </ContentWrapper>
    </AdminTemplate>
  );
};

export default MyProfile;
